import { inject, injectable } from "inversify";
import { ErrorCode, FlexSdkError, InternalError } from "~/modules/error";
import { Logger, LoggerFactory, loggerFactoryRTTI, LoggerName } from "~/modules/logger";
import { TelemetryEventGroup } from "~/modules/telemetry";
import {
    TelemetrySdkClient,
    telemetrySdkClientRTTI,
    TelemetrySdkEvent,
    TelemetrySdkEventGroup,
    TelemetrySdkEventName,
    TelemetrySdkEventSource
} from "~/modules/telemetrySdkClient";
import { RateLimiter } from "~/modules/ratelimiter/RateLimiter/RateLimiter";
import { RateLimiterOptions } from "~/modules/ratelimiter/RateLimiter/RateLimiterOptions";

const defaultOptions = {
    rateLimiterErrorCode: ErrorCode.TooManySDKRequests,
    disableFeatureErrorCode: ErrorCode.TemporarilyDisabled,
    disableFeatureThreshold: Number.MAX_VALUE
};




@injectable()
export class RateLimiterImpl implements RateLimiter {
    #options: Required<RateLimiterOptions>;

    #hitCountInTimeWindow: number = 0;

    #rateLimitExceededCount: number = 0;

    #resetTimeIntervalHandle: NodeJS.Timeout;

    readonly #telemetryEventGroup: TelemetryEventGroup<TelemetrySdkEvent>;

    readonly #logger: Logger;

    constructor(
        @inject(telemetrySdkClientRTTI) telemetryClient: TelemetrySdkClient,
        @inject(loggerFactoryRTTI) loggerFactory: LoggerFactory
    ) {
        this.#telemetryEventGroup = telemetryClient.createEventGroup(TelemetrySdkEventGroup.RateLimiter);
        this.#logger = loggerFactory(LoggerName.RateLimiter);
    }

    setup(options: RateLimiterOptions) {
        if (this.#options) {
            throw new InternalError("RateLimiter is already initialized");
        }

        if (options.timeWindow < 100) {
            throw new InternalError("timeWindow should be >= 100");
        }

        this.#options = {
            maxHitsInTimeWindow: options.maxHitsInTimeWindow,
            disableFeatureThreshold: options.disableFeatureThreshold || defaultOptions.disableFeatureThreshold,
            timeWindow: options.timeWindow,
            rateLimiterErrorCode: options.rateLimiterErrorCode || defaultOptions.rateLimiterErrorCode,
            disableFeatureErrorCode: options.disableFeatureErrorCode || defaultOptions.disableFeatureErrorCode,
            name: options.name
        };
        this.#resetTimeIntervalHandle = setInterval(this.#resetHitCountInTimeWindow, this.timeWindow);
    }

    get maxHitsInTimeWindow() {
        return this.#options.maxHitsInTimeWindow;
    }

    get hitCountInTimeWindow() {
        return this.#hitCountInTimeWindow;
    }

    get name() {
        return this.#options.name;
    }

    get timeWindow() {
        return this.#options.timeWindow;
    }

    check() {
        if (!this.#options) {
            throw new InternalError("Rate limiter not set up, call setup()");
        }

        this.#increaseHitCountInTimeWindow();
        this.#increaseExceededCountOnFirstTimeWindowLimitHit();

        this.#reportRateLimitErrorIfNeeded();
        this.#throwRateLimitErrorIfNeeded();
    }

    cleanup() {
        clearInterval(this.#resetTimeIntervalHandle);
    }

    #resetHitCountInTimeWindow = () => {
        this.#hitCountInTimeWindow = 0;
        this.#logger.trace(`rate limiter window reset. name="${this.name}"`);
    };

    #increaseHitCountInTimeWindow = () => {
        this.#hitCountInTimeWindow += 1;
    };

    #increaseExceededCountOnFirstTimeWindowLimitHit = () => {
        if (this.#hitCountInTimeWindow - 1 === this.#options.maxHitsInTimeWindow) {
            this.#rateLimitExceededCount += 1;
        }
    };

    #throwRateLimitErrorIfNeeded = () => {
        if (this.#rateLimitExceededCount >= this.#options.disableFeatureThreshold) {
            this.#logger.warn(
                `temporarily disabled. name="${this.name}", hitCount=${this.#hitCountInTimeWindow}, windowCount=${
                    this.#rateLimitExceededCount
                }`
            );
            throw new FlexSdkError(
                ErrorCode.TemporarilyDisabled,
                undefined,
                `Feature disabled by rate limiter ${this.name}`
            );
        }

        if (this.hitCountInTimeWindow > this.maxHitsInTimeWindow) {
            this.#logger.warn(`rate limiter hit. name="${this.name}", hitCount=${this.#hitCountInTimeWindow}`);
            throw new FlexSdkError(
                ErrorCode.TooManySDKRequests,
                undefined,
                `Request blocked by rate limiter ${this.name}`
            );
        }
    };

    #reportRateLimitErrorIfNeeded = () => {
        if (this.#hitCountInTimeWindow - 1 === this.#options.maxHitsInTimeWindow) {
            if (this.#rateLimitExceededCount === this.#options.disableFeatureThreshold) {
                
                this.#reportRateLimiterDisabledFeature();
            } else if (this.#rateLimitExceededCount < this.#options.disableFeatureThreshold) {
                
                this.#reportRateLimitHit();
            }
        }
    };

    #reportRateLimitHit = () => {
        this.#telemetryEventGroup.addEvents({
            eventName: TelemetrySdkEventName.RateLimiterTriggered,
            eventSource: TelemetrySdkEventSource.RateLimiter,
            resource: {
                id: this.#options.name
            }
        });
    };

    #reportRateLimiterDisabledFeature = () => {
        this.#telemetryEventGroup.addEvents({
            eventName: TelemetrySdkEventName.RateLimiterDisabledFeature,
            eventSource: TelemetrySdkEventSource.RateLimiter,
            resource: {
                id: this.#options.name
            }
        });
    };
}
